import { useState, useEffect } from "react";
import { HardDrive } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const AdminRequestsTab = ({ authToken, onMessage }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stationIds, setStationIds] = useState({});
  const headers = { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) };

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/requests`, { headers });
      if (res.ok) { const data = await res.json(); setRequests(data.requests || []); }
    } catch (err) { console.error('Admin fetch error:', err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRequests(); }, []);

  const handleApprove = async (requestId, userId) => {
    const stationId = stationIds[requestId];
    try {
      onMessage('Provisioning station...');
      const url = stationId
        ? `${BACKEND_URL}/api/admin/request/${requestId}?status=approved&station_id=${stationId}`
        : `${BACKEND_URL}/api/admin/request/${requestId}?status=approved`;
      const res = await fetch(url, { method: 'PUT', headers });
      if (res.ok) {
        const data = await res.json();
        if (data.provisioned) {
          onMessage(`Station auto-provisioned! ID: ${data.provisioned.station_id}, DJ Port: ${data.provisioned.dj_port}`);
        } else { onMessage('Request approved and station connected!'); }
        fetchRequests();
      } else { const err = await res.json(); onMessage(err.detail || 'Failed to approve'); }
    } catch { onMessage('Failed to update request'); }
  };

  const handleReject = async (requestId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/request/${requestId}?status=rejected`, { method: 'PUT', headers });
      if (res.ok) { onMessage('Request rejected'); fetchRequests(); }
    } catch { onMessage('Failed to reject'); }
  };

  if (loading) return <div className="flex justify-center py-12"><HardDrive className="w-8 h-8 text-purple-400 animate-pulse" /></div>;

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <div className="p-4 border-b border-white/10">
        <h2 className="text-lg font-semibold text-white">Station Requests ({requests.length})</h2>
      </div>
      {requests.length === 0 ? (
        <div className="p-8 text-center text-white/50">No station requests yet</div>
      ) : (
        <div className="divide-y divide-white/10">
          {requests.map((req) => (
            <div key={req.id} className="p-6">
              <div className="flex flex-wrap gap-6 justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : req.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{req.status.toUpperCase()}</span>
                    <span className="text-white font-medium">{req.station_name}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><p className="text-white/50">Customer Name</p><p className="text-white">{req.user_name}</p></div>
                    <div><p className="text-white/50">Email</p><p className="text-purple-400">{req.user_email}</p></div>
                    <div><p className="text-white/50">Plan</p><p className="text-white capitalize">{req.plan === 'premium' ? 'Premium' : req.plan}</p></div>
                    <div><p className="text-white/50">Payment Method</p><p className={req.payment_method === 'paypal' ? 'text-blue-400' : 'text-green-400'}>{req.payment_method === 'paypal' ? 'PayPal' : 'CashApp'}</p></div>
                  </div>
                  <p className="text-white/40 text-xs">Requested: {new Date(req.created_at).toLocaleString()}</p>
                </div>
                {req.status === 'pending' && (
                  <div className="flex flex-col gap-2">
                    <input type="text" placeholder="Station ID (optional)" value={stationIds[req.id] || ''} onChange={(e) => setStationIds({...stationIds, [req.id]: e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-white/30 text-sm w-48" data-testid={`station-id-input-${req.id}`} />
                    <button onClick={() => handleApprove(req.id, req.user_id)} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium" data-testid={`approve-${req.id}`}>
                      {stationIds[req.id] ? 'Approve & Connect' : 'Approve & Auto-Provision'}
                    </button>
                    <button onClick={() => handleReject(req.id)} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 px-4 py-2 rounded-lg text-sm" data-testid={`reject-${req.id}`}>Reject</button>
                    {!stationIds[req.id] && <p className="text-white/30 text-xs">Leave Station ID empty to auto-create on AzuraCast</p>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminRequestsTab;
